"use client";

import { useEffect, useState } from "react";
import { CubeIcon } from "@/components/guide-public/guide-ornaments";
import { GuidePhoto } from "@/components/guide-public/guide-parts";

/** Below this width the tour opens full screen instead of playing inline. */
const PHONE_QUERY = "(max-width: 767px)";

/**
 * A venue's 3D tour. On a wide screen it plays inline in the lapis frame. On
 * a phone the small inline viewer is crowded by the player's own controls,
 * so it shows the venue's photo with one "Walk through in 3D" button, and the tour
 * opens full screen over the page (no new page, no navigation), with a
 * close button. Until the page knows the screen size it shows the photo.
 */
export function GuideTourPlayer({
  src,
  title,
  poster,
  openLabel,
  closeLabel,
  venueName,
}: {
  src: string;
  title: string;
  poster: string | null;
  openLabel: string;
  closeLabel: string;
  venueName: string;
}) {
  const [phone, setPhone] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const update = () => setPhone(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  // Full screen: hold the page still behind the tour and close on Escape.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // A rotated phone or a widened window plays inline again.
  useEffect(() => {
    if (phone === false) setOpen(false);
  }, [phone]);

  if (phone === false) {
    return (
      <div className="eg-frame">
        <div className="eg-frame__inner" style={{ position: "relative", aspectRatio: "16 / 9", maxHeight: "78vh" }}>
          <iframe
            src={src}
            title={title}
            allow="fullscreen; xr-spatial-tracking"
            allowFullScreen
            loading="lazy"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="eg-frame">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="eg-frame__inner"
          aria-haspopup="dialog"
          style={{ position: "relative", display: "block", width: "100%", aspectRatio: "4 / 3", padding: 0, border: 0, cursor: "pointer", background: "var(--eg-lapis)" }}
        >
          <GuidePhoto src={poster} alt="" label="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          <span
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "14px 22px",
              borderRadius: 999,
              background: "var(--eg-ink)",
              color: "var(--eg-ground)",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "0 6px 20px rgba(22, 18, 14, 0.35)",
            }}
          >
            <span style={{ display: "inline-flex", flex: "none" }}>
              <CubeIcon size={18} />
            </span>
            {openLabel}
          </span>
        </button>
      </div>

      {open ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={title}
          style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", flexDirection: "column", background: "#000" }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "max(10px, env(safe-area-inset-top)) 14px 10px",
              background: "var(--eg-ink)",
              color: "var(--eg-ground)",
            }}
          >
            <span className="eg-display" style={{ fontSize: 18, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {venueName}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              autoFocus
              style={{
                flex: "none",
                minHeight: 40,
                padding: "0 16px",
                borderRadius: 999,
                border: "1px solid rgba(245, 240, 230, 0.4)",
                background: "transparent",
                color: "inherit",
                font: "inherit",
                cursor: "pointer",
              }}
            >
              {closeLabel} ✕
            </button>
          </div>
          <iframe
            src={src}
            title={title}
            allow="fullscreen; xr-spatial-tracking"
            allowFullScreen
            style={{ flex: 1, width: "100%", border: 0 }}
          />
        </div>
      ) : null}
    </>
  );
}
